import "./index.css";
import React, { useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useMediaQuery } from "react-responsive";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import ProgressBar from "@ramonak/react-progress-bar";
import { v4 as uuidv4 } from "uuid";
import { useLocation } from "react-router-dom";
import arrowDown from "../../assets/arrow_down.svg";
import LoyaltyDiscount from "./templates/LoyaltyDiscount";
import CouponDiscount from "./templates/CouponDiscount";
import { setMobileViewProperties } from "../../store/order.store";
import { fireGtagEvent } from "../../utils/google-analytics";
import { fireFbEvent } from "../../utils/facebook-analytics";
import freeShippingIcon from "../../assets/free-shippingIcon.svg";

function Cart() {
  const dispatch = useDispatch();
  const isMobile = useMediaQuery({ query: "(max-width: 767px)" });
  const { mobileView } = useSelector((state) => state.order);
  const config = useSelector((state) => state.config);

  const { shopifyCart, cartDetails, applyDiscountsApi } = useSelector(
    (state) => state.cart
  );

  const shippingAmount = useMemo(() => {
    if (
      cartDetails?.totalAmount > (config?.shipping_charge_max_threshold ?? 499)
    )
      return 0;
    return config?.shipping_charge_amount ?? 50;
  }, [config, cartDetails]);

  const ids = useMemo(() => Array.from({ length: 3 }).map(() => uuidv4()), []);

  React.useEffect(() => {
    fireGtagEvent("begin_checkout", {
      shopifyCart,
      cartDetails,
      applyDiscountsApi
    });
    fireFbEvent("InitiateCheckout", { shopifyCart, cartDetails });
  }, [shopifyCart, cartDetails, applyDiscountsApi]);

  const location = useLocation();
  const isPayment = location.pathname.includes("choose-type");
  const shippingStyle = config?.cod ? "both-payment" : "single-payment";
  const remainWorth = useMemo(
    () =>
      (config.shipping_charge_max_threshold - cartDetails.totalAmount).toFixed(
        2
      ),
    [config, cartDetails]
  );

  const progressData = useMemo(
    () =>
      (cartDetails.finalAmount / (config?.shipping_charge_max_threshold || 0)) *
      100,
    [config, cartDetails]
  );

  const formatCurrency = (amount) =>
    amount.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });

  if (!shopifyCart?.items?.length) {
    return (
      <div className="cart-container">
        <div
          className="bg_modal"
          onClick={() => {
            dispatch(setMobileViewProperties({ showOrderDetails: false }));
          }}
        />
        <div className="split-right-inner">
          <div className="split-right-upper">
            <h2 className="heading2-right">Order Summary</h2>
            <div className="products">
              {ids.map((id) => (
                <div className="product" key={id}>
                  <div className="product-img skeleton " />
                  <div className="product-desp">
                    <div>
                      <h3 className="skeleton skeleton-text">{"  "}</h3>
                    </div>
                    <div className="skeleton-heading">
                      <h3 className="skeleton skeleton-text skeleton-footer">
                        {" "}
                      </h3>
                      <h3 className="skeleton skeleton-text skeleton-footer">
                        {" "}
                      </h3>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="split-right-inner">
      <div
        className="bg_modal"
        onClick={() => {
          dispatch(setMobileViewProperties({ showOrderDetails: false }));
        }}
      />
      <div className="split-right-upper">
        <div
          className="order-details"
          onClick={() => {
            dispatch(
              setMobileViewProperties({
                showOrderDetails: !mobileView?.showOrderDetails
              })
            );
          }}
        >
          <h2 className="heading2-right pointer">Order Summary</h2>
          <span>
            {!!(mobileView?.showOrderDetails && isMobile) && (
              <img src={arrowDown} alt="down" />
            )}
          </span>
        </div>
        <div className="products">
          {shopifyCart?.items.map((item) => (
            <div className="product" key={`product_view_${item.title}`}>
              <div className="product-img">
                <img src={item.image} alt="" height="50" width="50" />
              </div>
              <div className="product-desp">
                <h3 className="heading3">{item.title}</h3>
                <h3 className="heading3-2">
                  {item.original_price !== item.final_price && (
                    <s>₹ {formatCurrency(item.original_price / 100)}</s>
                  )}
                  ₹ {formatCurrency(item.final_price / 100)}{" "}
                  <span> QTY - {item.quantity} </span>
                </h3>
                {!!item.discounts.length &&
                  item.discounts.map((discount) => (
                    <div className="local-offer">
                      <LocalOfferIcon fontSize="10" />
                      <h3 className="discount-label">
                        {discount.title}{" "}
                        {`(₹ ${formatCurrency(discount.amount / 100)})`}
                      </h3>
                    </div>
                  ))}
              </div>
            </div>
          ))}
        </div>
        {!!config?.bb_loyalty && <LoyaltyDiscount />}
        <CouponDiscount />
      </div>
      <div className="bill-section">
        <h1 className="heading-payment">Payment Detail</h1>
        <p>
          Sub Total{" "}
          <span>
            {" "}
            <strong className="ruppe">&#8377;</strong>{" "}
            {formatCurrency(cartDetails?.totalAmount)}
          </span>
        </p>
        {!!applyDiscountsApi?.discountCode && (
          <p>
            Promo Applied ({applyDiscountsApi?.discountCode})
            <span>₹{formatCurrency(applyDiscountsApi.couponDiscount)}</span>
          </p>
        )}
        {!!applyDiscountsApi.loyaltyPoints && (
          <p>
            Bluebees Point Applied
            <span>
              <strong className="ruppe">&#8377;</strong>{" "}
              {formatCurrency(Math.round(applyDiscountsApi.loyaltyPoints))}
            </span>
          </p>
        )}
        <p>
          Shipping Charges
          {shippingAmount ? (
            <span>
              {" "}
              <strong className="ruppe">&#8377;</strong>{" "}
              {formatCurrency(shippingAmount)}
            </span>
          ) : (
            <span>Free</span>
          )}
        </p>
      </div>
      {!!(shippingAmount && config?.shipping_charge && remainWorth > 0) && (
        <div className="free-shipping-cart">
          <p className="free-shipping-text-cart">
            Add products worth ₹ {formatCurrency(remainWorth)} more to get free
            shipping
          </p>
        </div>
      )}

      <div className="dash-section" />
      <div className="bill-section-total">
        Total Amount
        <span>
          {" "}
          <strong className="ruppe">&#8377;</strong>{" "}
          {formatCurrency(cartDetails.finalAmount + shippingAmount)}
        </span>
      </div>
      {cartDetails.totalAmount - cartDetails.finalAmount > 0 && (
        <div className="total-saving-container">
          <LocalOfferIcon fontSize="24" />
          <h3 className="total-saving">
            Total Saving{" "}
            {`(₹ ${formatCurrency(cartDetails.totalAmount - cartDetails.finalAmount)})`}
          </h3>
        </div>
      )}
      {!!(isPayment && config?.shipping_charge && remainWorth > 0) && (
        <div className={shippingStyle}>
          <div className="free-shipping-payment">
            <div>
              <img src={freeShippingIcon} alt="" />
            </div>
            <div className="free-shipping-text-payment">
              <p>
                Add products worth ₹ {formatCurrency(remainWorth)} more to get
              </p>
              <h1>Free Shipping</h1>
            </div>
          </div>
          <div className="progress-bar">
            <ProgressBar
              completed={progressData}
              bgColor="darkGreen"
              animateOnRender
              borderRadius="0"
              height="5px"
              isLabelVisible={false}
            />
          </div>
        </div>
      )}
    </div>
  );
}
export default Cart;
